import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ExternalLink, PanelRight, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  FieldGrid,
  FieldGridItem,
  NewTabLink,
  ParticipantAvatar,
  useEscapeClose,
  useFitHeight,
  useViewer
} from '@/design';
import { readViewerChoice, writeViewerChoice } from '@/design/viewer-store';
import type { IListFieldCatalogue, IListField, IListRow } from '../../api/lists-contracts';
import { NotesLog } from '../../components/notes-log';
import { recordSurveyPath } from '../../components/participant-link';
import { catalogueSections, listSurveyLabel } from '../../lists';
import { isNotesLog } from '../../notes-log';
import {
  CellValue,
  EditableCell,
  LONG_TEXT_WHOLE,
  SourceMark,
  cellText,
  editedText,
  type ICellEdit
} from './cells';
import { nameOf } from './editing';

/** The pane's two modes: the row read, or its editable cells as controls. */
export type PaneMode = 'view' | 'edit';

/** Where a row's *View* leads: the reading pane, or the record page in a new tab. */
type ViewOpens = 'pane' | 'tab';

const VIEW_OPENS_WORDS: Record<ViewOpens, string> = { pane: 'Reading pane', tab: 'New tab' };

/**
 * *Autosave my private views* (SR-412) — the reader's setting, on until they turn it off, kept on this
 * browser for the viewer as *Opens in* is. Off, a private view waits for *Save view* as a shared one
 * does.
 */
export function useAutosavePref(): [boolean, (on: boolean) => void] {
  const key = `veritant.lists.autosave.${useViewer()}`;
  const [chosen, setChosen] = React.useState<Nullable<{ key: string; on: boolean }>>(null);
  const stored = readViewerChoice(key).on;
  const on = chosen?.key === key ? chosen.on : stored !== false;
  const choose = React.useCallback(
    (next: boolean) => {
      setChosen({ key, on: next });
      writeViewerChoice(key, { on: next });
    },
    [key]
  );
  return [on, choose];
}

/** How the reading pane is laid out (SR-412): beside the list, below it, or a sheet over it. */
type PaneLayout = 'beside' | 'below' | 'sheet';

const LAYOUT_WORDS: Record<PaneLayout, string> = {
  beside: 'Beside the list',
  below: 'Below the list',
  sheet: 'Sheet over the list'
};
const LAYOUTS = Object.keys(LAYOUT_WORDS) as PaneLayout[];

/** From 90rem (1440 px) the list has room for the pane beside it. */
const WIDE = '(min-width: 90rem)';

/** Whether a media query matches, kept in step with the window — the pane's width here, the
 *  header's room on Lists (`area.tsx`). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export interface IPanePrefs {
  opens: ViewOpens;
  setOpens: (next: ViewOpens) => void;
  /** The layout in force: the reader's choice, else beside from 1440 px and a sheet below it. */
  layout: PaneLayout;
  setLayout: (next: PaneLayout) => void;
}

/**
 * The reader's reading-pane choices (SR-412) — where *View* leads and how the pane is laid out —
 * kept on this browser for the viewer as the work pages' page size is (`viewer-store.ts`). Until
 * they choose, *View* opens the pane, which sits beside the list from 1440 px and over it as a
 * sheet on a narrower screen.
 */
export function usePanePrefs(): IPanePrefs {
  const key = `veritant.lists.viewOpens.${useViewer()}`;
  const wide = useMediaQuery(WIDE);
  const [chosen, setChosen] = React.useState<Nullable<{ key: string; opens: ViewOpens; layout: Nullable<PaneLayout> }>>(null);
  const stored = readViewerChoice(key);
  const held = chosen?.key === key ? chosen : null;
  const opens: ViewOpens = held ? held.opens : stored.opens === 'tab' ? 'tab' : 'pane';
  const picked: Nullable<PaneLayout> = held
    ? held.layout
    : LAYOUTS.includes(stored.layout as PaneLayout)
      ? (stored.layout as PaneLayout)
      : null;
  const write = (next: { opens: ViewOpens; layout: Nullable<PaneLayout> }) => {
    setChosen({ key, ...next });
    writeViewerChoice(key, next.layout ? next : { opens: next.opens });
  };
  return {
    opens,
    setOpens: (next) => write({ opens: next, layout: picked }),
    layout: picked ?? (wide ? 'beside' : 'sheet'),
    setLayout: (next) => write({ opens, layout: next })
  };
}

/**
 * *Opens in* ▾ (SR-412): where a row's *View*, Edit and press lead — the reading pane or the record
 * page in a new tab — and how the pane is laid out, said on the button by its mode. There is no
 * pane toggle: every row opens the pane by its own icon, and the pane's ✕ or Esc closes it.
 */
export function PaneControl({ prefs }: { prefs: IPanePrefs }) {
  const { opens } = prefs;
  const Icon = opens === 'tab' ? ExternalLink : PanelRight;
  return (
    <span className="inline-flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 px-2.5 font-normal"
            aria-label={`Opens in: ${VIEW_OPENS_WORDS[opens]}`}
            title={`Opens in: ${VIEW_OPENS_WORDS[opens]}`}
          >
            <Icon className="size-4" aria-hidden />
            Opens in
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">View on a row opens</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={opens} onValueChange={(next) => prefs.setOpens(next as ViewOpens)}>
            <DropdownMenuRadioItem value="pane">Reading pane</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="tab">New tab</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Reading pane</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={prefs.layout} onValueChange={(next) => prefs.setLayout(next as PaneLayout)}>
            {LAYOUTS.map((layout) => (
              <DropdownMenuRadioItem key={layout} value={layout}>
                {LAYOUT_WORDS[layout]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

/**
 * The reading pane (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.9; the owner's Q5): **the row as a record** — the view's columns as label-over-value pairs, in
 * the catalogue's groups, under the person's name — and one link, **Open the record page**, to the
 * row's survey on the record page, in a new tab (SR-491). **Edit** draws the editable cells as controls, each staged exactly as a cell's — one
 * draft per cell, two places to make it.
 *
 * **Its width follows the screen's** — `min(34vw, 40rem)` beside the grid — and its fields the
 * pane's own (`FieldGrid`'s `framed`): one to a line until its content is 34rem wide (a pane some
 * 36rem wide), so a control takes the pane's width rather than half of a fixed 26rem.
 *
 * The head is initials, never a photo: the row carries whether a photo exists (`sys.photo`), not
 * its address. The grid stays the page's one `main`, so this is the screen's own region beside it
 * and not `SplitView`; **Esc closes it by the split view's rule** (`escapeIsFree`) — never out of a
 * field, never while a menu, listbox, popover or dialog is open.
 */
export function ReadingPane({
  row,
  columns,
  catalogue,
  mode,
  onMode,
  onClose,
  editOf,
  editLocked
}: {
  row: IListRow;
  /** The view's columns shown — what the record draws. */
  columns: readonly IListField[];
  catalogue: IListFieldCatalogue;
  mode: PaneMode;
  onMode: (mode: PaneMode) => void;
  onClose: () => void;
  /** A cell's edit, or null where it is read-only to this reader. */
  editOf: (row: IListRow, field: IListField) => Nullable<ICellEdit>;
  /** Why *Edit* is not offered — `NotPermitted`'s id, the reason drawn once on the screen. */
  editLocked: Nullable<string>;
}) {
  const titleId = React.useId();
  // Beside the grid it ends where the window does, as the grid's frame does (SR-439): its
  // `calc(100dvh - 5rem)` reached past the window's bottom from below the filters.
  const section = React.useRef<HTMLElement>(null);
  const fitted = useFitHeight(section);
  const name = nameOf(row);
  const shown = new Set(columns.map((field) => field.code));
  const sections = catalogueSections(catalogue, (field) => shown.has(field.code), shown);

  useEscapeClose(onClose);

  return (
    <section
      aria-labelledby={titleId}
      data-pane={mode}
      data-participant={row.participantId}
      // Under the grid on a narrower screen, reached by scrolling: not what the grid fits above.
      data-fit-skip=""
      ref={section}
      style={fitted === null ? undefined : ({ '--pane-fit': `${fitted}px` } as React.CSSProperties)}
      className="@container grid content-start gap-4 rounded-md border border-border bg-card p-4 min-[90rem]:sticky min-[90rem]:top-16 min-[90rem]:max-h-[var(--pane-fit,calc(100dvh-5rem))] min-[90rem]:overflow-y-auto"
    >
      <div className="flex items-start gap-3">
        <ParticipantAvatar photoUri={null} displayName={name} size="md" decorative />
        <div className="grid min-w-0 flex-1 gap-0.5">
          <h2 id={titleId} className="truncate text-base font-medium text-[var(--color-royal-blue)]">
            {name}
          </h2>
          <span className="text-xs text-muted-foreground">{listSurveyLabel(row.survey)}</span>
          <NewTabLink
            to={recordSurveyPath(row.participantId, row.survey)}
            label="Open the record page in a new tab"
            className="justify-self-start px-0 text-sm underline-offset-4"
          >
            Open the record page
          </NewTabLink>
        </div>
        <Button asChild variant="ghost" size="icon" className="size-8">
          <Link
            to={recordSurveyPath(row.participantId, row.survey)}
            target="_blank"
            rel="noopener"
            aria-label="Open in new tab"
            title="Open in new tab"
            data-pane-new-tab=""
          >
            <ExternalLink className="size-4" aria-hidden />
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="Close the reading pane"
          onClick={onClose}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>

      <div role="group" aria-label="Pane mode" className="flex gap-1 text-[0.8125rem]">
        {(['view', 'edit'] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            aria-pressed={mode === choice}
            aria-disabled={choice === 'edit' && editLocked !== null ? true : undefined}
            aria-describedby={choice === 'edit' && editLocked !== null ? editLocked : undefined}
            onClick={() => {
              if (choice === 'edit' && editLocked !== null) return;
              onMode(choice);
            }}
            className={cn(
              'h-7 rounded-full border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === choice
                ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] font-medium text-[var(--color-royal-blue)]'
                : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]',
              choice === 'edit' && editLocked !== null && 'cursor-not-allowed opacity-60'
            )}
          >
            {choice === 'view' ? 'View' : 'Edit'}
          </button>
        ))}
      </div>

      {sections.length === 0 && (
        <p className="text-sm text-muted-foreground">This view shows no columns to read here.</p>
      )}
      {sections.map((section) => (
        <div key={section.key} className="grid gap-2">
          <h3 className="text-xs font-medium tracking-wide text-[var(--veritant-royal-70)] uppercase">
            {section.title}
          </h3>
          <FieldGrid framed>
            {section.fields.map((field) => {
              const edit = editOf(row, field);
              const cell = row.cells[field.code];
              // A notes log as its entries, their initials named on hover (SR-494).
              const log =
                field.kind === 'long_text' && isNotesLog(field.code) && typeof cell?.v === 'string'
                  ? cell.v
                  : null;
              const label = field.number ? `${field.number} ${field.label}` : field.label;
              return (
                <FieldGridItem
                  key={field.code}
                  label={label}
                  filled={
                    (mode === 'edit' && edit !== null) ||
                    edit?.draft !== undefined ||
                    cellText(field, row.cells[field.code]) !== ''
                  }
                >
                  {mode === 'edit' && edit ? (
                    <EditableCell field={field} row={row} name={name} edit={edit} form />
                  ) : edit?.draft ? (
                    <span
                      data-cell={field.code}
                      data-pending=""
                      className={cn(
                        'block pl-1 shadow-[inset_3px_0_0_var(--color-yellow)]',
                        field.kind === 'long_text' && LONG_TEXT_WHOLE
                      )}
                    >
                      {editedText(field, row.cells[field.code], edit)}
                    </span>
                  ) : log !== null ? (
                    <span data-cell={field.code} className="block">
                      <NotesLog notes={log} startOpen />
                      {cell?.src === 'salesforce' && <SourceMark />}
                    </span>
                  ) : (
                    <CellValue field={field} cell={row.cells[field.code]} look={null} whole />
                  )}
                </FieldGridItem>
              );
            })}
          </FieldGrid>
        </div>
      ))}
    </section>
  );
}
